<script lang="ts">
  import FormatMoney from "@shared/svelte/Components/FormatMoney.svelte";
  import productContainerDetails from "@shared/filters/product_container_details.js";
  import { slide } from "svelte/transition";
  import ChartWidget from "@modern/components/retailer/reports/ChartWidget.svelte";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import { OnLayoutChange } from "@shared/svelte/Directives/OnLayoutChange";
  import type { ProductDetail, ReportDetailsMonth } from "@shared/types/Reports";
  import { pluralize } from "@shared/svelte/Modules/pluralize";
  import Arrow from "@shared/svelte/Components/Arrow.svelte";
  import { get } from "lodash";

  export let reportsOrdersDetail: ProductDetail[];
  export let startDate;
  export let endDate;

  let categoriesData = {};
  let productsDetailData = {};
  let monthsDetailData = {};
  let showChart = {};

  $: {
    reportsOrdersDetail.forEach(function (d) {
      HttpPromise({
        url: "/api/retailer/reports/overview_categories",
        method: "GET",
        data: {
          start_date: startDate,
          end_date: endDate,
          distributor_id: d.distributor_id,
        },
      }).then(function (res) {
        categoriesData[d.distributor_id] = res;
      });
    });
  }

  const getDetail = product_line_id => {
    if (!productsDetailData[product_line_id]) {
      monthsDetailData[product_line_id] = {};
      HttpPromise({
        url: "/api/retailer/reports/products_detail",
        method: "GET",
        data: {
          start_date: startDate,
          end_date: endDate,
          product_line_id: product_line_id,
        },
      }).then(function (res) {
        productsDetailData[product_line_id] = res;
        productsDetailData[product_line_id]["show"] = true;
      });
      return;
    }
  };

  let layoutName = "desktop";
  const updateLayoutName = name => (layoutName = name);

  const productQuantity = product => {
    if (product == null || product == undefined) {
      return;
    }
    var res = [];
    if (product.unit_quantity > 0) {
      if (product.container_type == "other") {
        res.push(product.unit_quantity);
      } else {
        for (const container_type of product.container_type) {
          let quantity = product.container_counts[container_type];
          if (quantity) {
            res.push(`${quantity}` + " " + pluralize(container_type, quantity));
          }
        }
      }
    }
    if (product.case_quantity > 0) {
      res.push(product.case_quantity + " " + pluralize("case", product.case_quantity));
    }
    return res.join(", ");
  };
  const expandRow = async id => {
    await getDetail(id);

    try {
      productsDetailData[id].show = !productsDetailData[id]?.show;
    } catch {}
  };

  const getByMonth = (productLineId, productDetailsId) => {
    if (!get(monthsDetailData, [productDetailsId])) {
      return HttpPromise({
        url: "/api/retailer/reports/products_detail_months",
        method: "GET",
        data: {
          product_id: productDetailsId,
          product_line_id: productLineId,
        },
      }).then(
        function success(res: ReportDetailsMonth[]) {
          monthsDetailData[productDetailsId] = res;
          showChart[productDetailsId] = true;
        },
        function error(res) {
          monthsDetailData[productDetailsId] = res;
        },
      );
    }

    showChart[productDetailsId] = !showChart[productDetailsId];
  };
</script>

{#if !reportsOrdersDetail.length}
  <div class="grid-container margin-bottom--medium">
    <div class="grid-row margin-top--small">
      <div class="grid-col c12-12">
        <div class="background-white padding--smaller report-dashboard__container">
          <div class="s3 align-center">No data found for the dates selected.</div>
        </div>
      </div>
    </div>
  </div>
{/if}

<div use:OnLayoutChange={updateLayoutName} class="grid-container margin-top--small">
  {#each reportsOrdersDetail as product}
    <div id={`report_product_line_${product.product_line_id}`}>
      <div class="grid-col c12-12">
        <div class="padding--smaller">
          <div class="grid-row position-relative">
            <div
              class="grid-col c12-12 cursor-pointer"
              on:click={async () => await expandRow(product.product_line_id)}
            >
              <!-- Desktop Product Lines Header -->
              <div class="grid-row padding-top--smaller">
                <div class="grid-col c3-12 vertical-align-bottom">
                  <div class="default-body--bold notranslate">{product.name}</div>
                </div>
                <div class="grid-col c3-12 vertical-align-bottom">
                  <div class="default-small--bold uppercase">Times Ordered</div>
                </div>
                <div class="grid-col c3-12 vertical-align-bottom">
                  <div class="default-small--bold uppercase">QTY</div>
                </div>
                <div class="grid-col c3-12 vertical-align-bottom">
                  <div class="default-small--bold uppercase">Total Spend</div>
                </div>
              </div>
              <div
                class="grid-row margin-top--smallest margin-bottom--smaller padding-bottom--smaller"
              >
                <div class="grid-col c3-12 vertical-align-bottom">
                  <div class="default-body--regular product_line-report-distributor notranslate">
                    {product.distributor_name}
                  </div>
                </div>
                <div class="grid-col c3-12 vertical-align-bottom">
                  <div class="default-body--regular product_line-report-num-orders">
                    {product.times_ordered}
                  </div>
                </div>
                <div class="grid-col c3-12 vertical-align-bottom">
                  <div class="default-body--regular product_line-report-quantity">
                    {productQuantity(product)}
                  </div>
                </div>
                <div class="grid-col c2-12 vertical-align-bottom">
                  <div class="default-body--regular color-green product_line-report-spend">
                    <FormatMoney input={product.spend} />
                  </div>
                </div>
                <div class="grid-col c1-12 report-products__product-line-expand">
                  <Arrow
                    direction={productsDetailData[product.product_line_id]?.show ? "up" : "down"}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {#if productsDetailData[product.product_line_id]?.show}
        <div transition:slide class="grid-col c12-12">
          {#each [productsDetailData[product.product_line_id][0]] as productDetails}
            <div
              id={`report_product_line_details_${product.product_line_id}`}
              class="grid-col c12-12 cursor-pointer report-product-line__details"
              on:click={() => getByMonth(product.product_line_id, productDetails.id)}
            >
              <div class="report-product__header--separator" />
              <div class="grid-row margin-top--small margin-bottom--smaller">
                <div class="grid-col c3-12 vertical-align-bottom">
                  <div class="secondary-small--regular uppercase">Specifications</div>
                </div>
                <div class="grid-col c3-12 vertical-align-bottom">
                  <div class="secondary-small--regular uppercase">Times Ordered</div>
                </div>
                <div class="grid-col c3-12 vertical-align-bottom">
                  <div class="secondary-small--regular uppercase">QTY</div>
                </div>
                <div class="grid-col c2-12 vertical-align-bottom">
                  <div class="secondary-small--regular uppercase">Spend</div>
                </div>
              </div>
              <div
                class="grid-row padding-bottom--small padding-top--small report-product__product-line position-relative"
              >
                <div class="grid-col c3-12 vertical-align-bottom">
                  <div class="default-body--regular">
                    {productContainerDetails()(productDetails, null, null, true)}
                  </div>
                </div>
                <div class="grid-col c3-12 vertical-align-bottom">
                  <div class="default-body--regular">
                    {productDetails.times_ordered}
                  </div>
                </div>
                <div class="grid-col c3-12 vertical-align-bottom">
                  <div class="default-body--regular">
                    {productDetails.quantity}
                  </div>
                </div>
                <div class="grid-col c2-12 vertical-align-bottom">
                  <div class="default-body--regular color-green">
                    <FormatMoney input={productDetails.spend} />
                  </div>
                </div>
                <div class="grid-col c1-12">
                  <Arrow direction={showChart[productDetails.id] ? "up" : "down"} />
                </div>
                {#if showChart[productDetails.id]}
                  <div
                    class="grid-col c10-10 padding-top--small padding-bottom--small vertical-align-middle"
                    transition:slide
                  >
                    <ChartWidget
                      purchaseDetailsByMonth={monthsDetailData[productDetails.id]}
                      layoutName="desktop"
                    />
                  </div>
                {/if}
              </div>
            </div>
          {/each}
        </div>
      {/if}
    </div>
  {/each}
</div>
